/**
 * Tipping & Round-Up Donation Component
 * Card tipping with suggested amounts + round-up donations to ASPCA/Riley's
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Heart, DollarSign, Loader2, CheckCircle2, Gift, 
  ArrowRight, Sparkles, Info
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Charity logos/emojis
const CHARITIES = {
  aspca: {
    name: 'ASPCA',
    fullName: 'American Society for the Prevention of Cruelty to Animals',
    emoji: '🐾',
    color: 'orange',
    description: 'Helping animals find loving homes'
  },
  rileys: {
    name: "Riley's Children's Hospital",
    fullName: 'Riley Children\'s Foundation',
    emoji: '🏥',
    color: 'blue',
    description: "Indiana's premier children's hospital"
  }
};

export default function TippingDonation({ 
  serviceRequestId, 
  serviceTotal, 
  onTipAdded, 
  onDonationAdded,
  compact = false 
}) {
  const { getAuthHeaders } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [customTip, setCustomTip] = useState('');
  const [selectedTipPercent, setSelectedTipPercent] = useState(null);
  const [donationType, setDonationType] = useState(null); // aspca, rileys, both, none
  const [tipAdded, setTipAdded] = useState(false);
  const [donationAdded, setDonationAdded] = useState(false);
  const [currentTip, setCurrentTip] = useState(0);
  const [currentDonation, setCurrentDonation] = useState(0);

  // Fetch tip suggestions on mount
  useEffect(() => {
    if (serviceTotal > 0) {
      fetchSuggestions();
    }
  }, [serviceTotal]);

  const fetchSuggestions = async () => {
    try {
      const response = await axios.get(
        `${API}/tips/suggestions/${serviceTotal}`,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setSuggestions(response.data);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
      // Generate local suggestions as fallback
      setSuggestions({
        tip_suggestions: [
          { percent: 15, amount: Math.round(serviceTotal * 0.15 * 100) / 100, label: 'Good (15%)' },
          { percent: 18, amount: Math.round(serviceTotal * 0.18 * 100) / 100, label: 'Great (18%)' },
          { percent: 20, amount: Math.round(serviceTotal * 0.20 * 100) / 100, label: 'Excellent (20%)' },
          { percent: 25, amount: Math.round(serviceTotal * 0.25 * 100) / 100, label: 'Outstanding (25%)' }
        ],
        roundup_info: {
          roundup_amount: Math.ceil(serviceTotal) - serviceTotal || 1
        }
      });
    }
  };

  // Add tip
  const addTip = async (amount, tipType = 'custom') => {
    if (amount <= 0) {
      toast.error('Please enter a valid tip amount');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/tips/add`,
        {
          service_request_id: serviceRequestId,
          tip_amount: amount,
          tip_type: tipType
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      setCurrentTip(response.data.total_tips);
      setTipAdded(true);
      toast.success(`$${amount.toFixed(2)} tip added! Thank you for your generosity.`);
      
      if (onTipAdded) {
        onTipAdded(response.data.total_tips);
      }
    } catch (error) {
      toast.error('Failed to add tip. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Add round-up donation
  const addDonation = async (type) => {
    if (!type || type === 'none') {
      setDonationType('none');
      setDonationAdded(true);
      if (onDonationAdded) {
        onDonationAdded(0);
      }
      return;
    }

    setLoading(true);
    try {
      const totalWithTip = serviceTotal + currentTip;
      
      const response = await axios.post(
        `${API}/tips/roundup-donation`,
        {
          service_request_id: serviceRequestId,
          donation_type: type,
          total_before_roundup: totalWithTip
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      setDonationType(type);
      setCurrentDonation(response.data.roundup_amount);
      setDonationAdded(true);
      
      if (type !== 'none') {
        toast.success(response.data.message);
      }
      
      if (onDonationAdded) {
        onDonationAdded(response.data.roundup_amount);
      }
    } catch (error) {
      toast.error('Failed to process donation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const roundupAmount = suggestions?.roundup_info?.roundup_amount || 
    (Math.ceil(serviceTotal + currentTip) - (serviceTotal + currentTip)) || 1;

  // Compact view for checkout flow
  if (compact) {
    return (
      <div className="space-y-4" data-testid="tipping-compact">
        {/* Quick Tip Selection */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <Label className="font-medium flex items-center gap-2">
              <Heart className="w-4 h-4 text-pink-500" />
              Add a tip for your provider
            </Label>
            {tipAdded && (
              <Badge className="bg-green-100 text-green-700">
                ${currentTip.toFixed(2)} added
              </Badge>
            )}
          </div>
          
          <div className="flex gap-2 flex-wrap">
            {suggestions?.tip_suggestions?.slice(0, 3).map((tip) => (
              <Button
                key={tip.percent}
                variant={selectedTipPercent === tip.percent ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setSelectedTipPercent(tip.percent);
                  addTip(tip.amount, `suggested_${tip.percent}`);
                }}
                disabled={loading || tipAdded}
                className="flex-1"
              >
                ${tip.amount.toFixed(2)}
                <span className="text-xs ml-1 opacity-70">{tip.percent}%</span>
              </Button>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedTipPercent('custom')}
              disabled={tipAdded}
              className="flex-1"
            >
              Custom
            </Button>
          </div>
          
          {selectedTipPercent === 'custom' && !tipAdded && (
            <div className="flex gap-2 mt-3">
              <div className="relative flex-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="number"
                  value={customTip}
                  onChange={(e) => setCustomTip(e.target.value)}
                  placeholder="Enter amount"
                  className="pl-8"
                />
              </div>
              <Button
                onClick={() => addTip(parseFloat(customTip))}
                disabled={loading || !customTip}
              >
                Add
              </Button>
            </div>
          )}
        </div>

        {/* Round-Up Donation */}
        <div className="p-4 bg-gradient-to-r from-orange-50 to-blue-50 rounded-lg border border-orange-100">
          <div className="flex items-center justify-between mb-3">
            <Label className="font-medium flex items-center gap-2">
              <Gift className="w-4 h-4 text-purple-500" />
              Round up for charity
            </Label>
            {donationAdded && donationType !== 'none' && (
              <Badge className="bg-purple-100 text-purple-700">
                ${roundupAmount.toFixed(2)} donated
              </Badge>
            )}
          </div>
          
          {!donationAdded ? (
            <>
              <p className="text-sm text-gray-600 mb-3">
                Round up ${(serviceTotal + currentTip).toFixed(2)} to ${Math.ceil(serviceTotal + currentTip).toFixed(2)} 
                <span className="font-medium text-purple-600"> (+${roundupAmount.toFixed(2)})</span>
              </p>
              
              <div className="grid grid-cols-2 gap-2 mb-2">
                <Button
                  variant={donationType === 'aspca' ? "default" : "outline"}
                  size="sm"
                  onClick={() => addDonation('aspca')}
                  disabled={loading}
                  className="text-xs"
                >
                  {CHARITIES.aspca.emoji} ASPCA
                </Button>
                <Button
                  variant={donationType === 'rileys' ? "default" : "outline"}
                  size="sm"
                  onClick={() => addDonation('rileys')}
                  disabled={loading}
                  className="text-xs"
                >
                  {CHARITIES.rileys.emoji} Riley's
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant={donationType === 'both' ? "default" : "outline"}
                  size="sm"
                  onClick={() => addDonation('both')}
                  disabled={loading}
                  className="text-xs"
                >
                  <Sparkles className="w-3 h-3 mr-1" />
                  Both (50/50)
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => addDonation('none')}
                  disabled={loading}
                  className="text-xs text-gray-500"
                >
                  No thanks
                </Button>
              </div>
            </>
          ) : (
            donationType !== 'none' && (
              <div className="text-sm text-gray-600">
                <CheckCircle2 className="w-4 h-4 inline mr-1 text-green-500" />
                Donating to: {donationType === 'both' ? 'ASPCA & Riley\'s (50/50)' : 
                  donationType === 'aspca' ? 'ASPCA' : "Riley's Children's Hospital"}
              </div>
            )
          )}
        </div>
      </div>
    );
  }

  // Full view
  return (
    <Card className="w-full" data-testid="tipping-donation">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-pink-500" />
          Tip & Donate
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Service Total */}
        <div className="text-center p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-500">Service Total</p>
          <p className="text-3xl font-bold">${serviceTotal.toFixed(2)}</p>
        </div>

        {/* Tipping Section */}
        <div>
          <Label className="text-lg font-semibold mb-3 block">
            Add a tip for your service provider
          </Label>
          <p className="text-sm text-gray-600 mb-4">
            Tips go 100% to your provider. Your generosity is appreciated!
          </p>
          
          {/* Suggested Amounts */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            {suggestions?.tip_suggestions?.map((tip) => (
              <button
                key={tip.percent}
                onClick={() => {
                  setSelectedTipPercent(tip.percent);
                  if (!tipAdded) addTip(tip.amount, `suggested_${tip.percent}`);
                }}
                disabled={loading || tipAdded}
                className={`p-4 border-2 rounded-xl transition-all ${
                  selectedTipPercent === tip.percent
                    ? 'border-pink-500 bg-pink-50'
                    : 'border-gray-200 hover:border-pink-300'
                } ${tipAdded ? 'opacity-50' : ''}`}
              >
                <p className="text-2xl font-bold">${tip.amount.toFixed(2)}</p>
                <p className="text-sm text-gray-500">{tip.label}</p>
              </button>
            ))}
          </div>

          {/* Custom Tip */}
          {!tipAdded && (
            <div className="flex gap-3">
              <div className="relative flex-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="number"
                  value={customTip}
                  onChange={(e) => {
                    setCustomTip(e.target.value);
                    setSelectedTipPercent('custom');
                  }}
                  placeholder="Custom amount"
                  className="pl-8"
                />
              </div>
              <Button
                onClick={() => addTip(parseFloat(customTip))}
                disabled={loading || !customTip}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Tip'}
              </Button>
            </div>
          )}

          {/* No Tip Option */}
          {!tipAdded && (
            <button
              onClick={() => {
                setTipAdded(true);
                if (onTipAdded) onTipAdded(0);
              }}
              className="w-full mt-3 text-center text-sm text-gray-500 hover:text-gray-700"
            >
              Skip tipping
            </button>
          )}

          {tipAdded && currentTip > 0 && (
            <div className="mt-4 p-3 bg-green-50 rounded-lg flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <span className="text-green-800">
                ${currentTip.toFixed(2)} tip added. Thank you!
              </span>
            </div>
          )}
        </div>

        {/* Donation Section */}
        <div className="pt-6 border-t">
          <Label className="text-lg font-semibold mb-3 block flex items-center gap-2">
            <Gift className="w-5 h-5 text-purple-500" />
            Round Up for Charity
          </Label>
          
          <p className="text-sm text-gray-600 mb-4">
            Round up your total to the nearest dollar and donate to local charities.
            All donations are <strong>tax-deductible</strong>.
          </p>

          {/* Round Up Preview */}
          <div className="p-4 bg-gradient-to-r from-orange-50 to-blue-50 rounded-xl mb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Current Total</p>
                <p className="text-xl font-bold">${(serviceTotal + currentTip).toFixed(2)}</p>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">Rounded Up</p>
                <p className="text-xl font-bold text-purple-600">
                  ${Math.ceil(serviceTotal + currentTip).toFixed(2)}
                </p>
              </div>
            </div>
            <p className="text-center mt-2 text-sm text-purple-600 font-medium">
              +${roundupAmount.toFixed(2)} donation
            </p>
          </div>

          {/* Charity Selection */}
          {!donationAdded ? (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">Choose where to donate:</p>
              
              {/* ASPCA */}
              <button
                onClick={() => addDonation('aspca')}
                disabled={loading}
                className={`w-full p-4 border-2 rounded-xl transition-all text-left ${
                  donationType === 'aspca' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{CHARITIES.aspca.emoji}</span>
                  <div>
                    <p className="font-semibold">{CHARITIES.aspca.name}</p>
                    <p className="text-sm text-gray-500">{CHARITIES.aspca.description}</p>
                  </div>
                </div>
              </button>

              {/* Riley's */}
              <button
                onClick={() => addDonation('rileys')}
                disabled={loading}
                className={`w-full p-4 border-2 rounded-xl transition-all text-left ${
                  donationType === 'rileys' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{CHARITIES.rileys.emoji}</span>
                  <div>
                    <p className="font-semibold">{CHARITIES.rileys.name}</p>
                    <p className="text-sm text-gray-500">{CHARITIES.rileys.description}</p>
                  </div>
                </div>
              </button>

              {/* Both */}
              <button
                onClick={() => addDonation('both')}
                disabled={loading}
                className={`w-full p-4 border-2 rounded-xl transition-all text-left ${
                  donationType === 'both' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl">💝</span>
                  <div>
                    <p className="font-semibold">Split Between Both (50/50)</p>
                    <p className="text-sm text-gray-500">
                      ${(roundupAmount / 2).toFixed(2)} to each organization
                    </p>
                  </div>
                </div>
              </button>

              {/* No Donation */}
              <button
                onClick={() => addDonation('none')}
                className="w-full text-center text-sm text-gray-500 hover:text-gray-700 py-2"
              >
                No donation this time
              </button>
            </div>
          ) : (
            <div className={`p-4 rounded-lg ${
              donationType !== 'none' ? 'bg-purple-50' : 'bg-gray-50'
            }`}>
              {donationType !== 'none' ? (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="font-medium text-purple-800">
                      ${currentDonation.toFixed(2)} donation added
                    </p>
                    <p className="text-sm text-purple-600">
                      Going to: {donationType === 'both' ? 'ASPCA & Riley\'s (50/50 split)' : 
                        donationType === 'aspca' ? 'ASPCA' : "Riley's Children's Hospital"}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-600 text-center">
                  No donation added. Thank you for considering!
                </p>
              )}
            </div>
          )}

          {/* Tax Info */}
          <div className="mt-4 flex items-start gap-2 text-xs text-gray-500">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p>
              Donations are tax-deductible. You'll receive a receipt for your records. 
              100% of your donation goes to the selected organization(s).
            </p>
          </div>
        </div>

        {/* Final Summary */}
        {(tipAdded || donationAdded) && (
          <div className="pt-4 border-t">
            <div className="flex justify-between text-sm mb-1">
              <span>Service</span>
              <span>${serviceTotal.toFixed(2)}</span>
            </div>
            {currentTip > 0 && (
              <div className="flex justify-between text-sm mb-1">
                <span>Tip</span>
                <span className="text-pink-600">+${currentTip.toFixed(2)}</span>
              </div>
            )}
            {currentDonation > 0 && (
              <div className="flex justify-between text-sm mb-1">
                <span>Donation</span>
                <span className="text-purple-600">+${currentDonation.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-lg pt-2 border-t mt-2">
              <span>New Total</span>
              <span>${(serviceTotal + currentTip + currentDonation).toFixed(2)}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
